import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus, Clock } from 'lucide-react';
import { animalData } from '../../data/animals';
import { useCart } from '../../context/CartContext';

const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: p % 1 === 0 ? 0 : 2 }).format(p);

function EncounterCard({ animal, index }) {
  const { addItem } = useCart();
  const [hours, setHours] = useState(1);
  const hasHourly = animal.additionalHourPrice > 0;
  const total = hasHourly ? animal.price + (animal.additionalHourPrice * (hours - 1)) : animal.price;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group"
      data-testid={`card-${animal.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden mb-4">
        <img src={animal.image} alt={animal.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={animal.imagePosition ? { objectPosition: animal.imagePosition } : undefined} />
        <div className="absolute top-4 right-4 bg-background/80 backdrop-blur-sm px-3 py-1.5 border border-white/10">
          <span className="text-gold text-sm font-medium">{fmt(animal.price)}</span>
        </div>
      </div>

      <h3 className="font-display text-xl md:text-2xl text-white group-hover:text-gold transition-colors duration-300 mb-2">
        {animal.name}
      </h3>
      <p className="text-white/50 text-sm mb-3 leading-relaxed">{animal.description}</p>

      {hasHourly && (
        <p className="text-white/40 text-xs mb-3">+{fmt(animal.additionalHourPrice)} each additional hour</p>
      )}

      {hasHourly && (
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center gap-1 bg-surface/60 border border-white/10 px-2 py-1">
            <Clock className="w-3 h-3 text-white/50" />
            <button onClick={() => setHours(Math.max(1, hours - 1))} className="p-1 text-white/50 hover:text-white">
              <Minus className="w-3 h-3" />
            </button>
            <span className="text-white text-xs w-10 text-center">{hours} hr{hours > 1 ? 's' : ''}</span>
            <button onClick={() => setHours(hours + 1)} className="p-1 text-white/50 hover:text-white">
              <Plus className="w-3 h-3" />
            </button>
          </div>
          {hours > 1 && <span className="text-gold text-xs font-medium">{fmt(total)}</span>}
        </div>
      )}

      <button
        data-testid={`add-${animal.id}`}
        onClick={() => { addItem({ ...animal, hours }); setHours(1); }}
        className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-background text-xs font-semibold uppercase tracking-wide hover:bg-gold-light transition-colors duration-300"
      >
        <Plus className="w-3 h-3" /> Add to Estimate
      </button>
    </motion.div>
  );
}

function ExoticPackageSection() {
  const { addItem } = useCart();
  const [selectedTeam, setSelectedTeam] = useState(null);
  const pkg = animalData.handsOnEncounters[3]; // full-interactive

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="mt-20 pt-16 border-t border-white/10"
      data-testid="exotic-package-section"
    >
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        {/* Left: Image */}
        <div className="relative overflow-hidden">
          <img
            src={pkg.image}
            alt={pkg.name}
            className="w-full aspect-[3/2] object-cover"
          />
          <div className="absolute top-4 right-4 bg-background/80 backdrop-blur-sm px-4 py-2 border border-white/10">
            <span className="text-gold text-lg font-medium">{fmt(pkg.price)}</span>
          </div>
        </div>

        {/* Right: Details + Team Lists */}
        <div>
          <h3 className="font-display text-3xl md:text-4xl text-white mb-3">
            Exotic Animal Package
          </h3>
          <p className="text-gold text-sm font-medium mb-4">(7-8) Interactive Animals</p>
          <p className="text-white/60 text-sm leading-relaxed mb-6">
            {pkg.description}
          </p>

          {/* Team Selection */}
          <p className="text-white text-sm font-medium mb-3">Select Your Team</p>
          <div className="grid grid-cols-2 gap-4 mb-8">
            <button
              onClick={() => setSelectedTeam('team1')}
              className={`p-4 border text-left transition-all duration-300 ${
                selectedTeam === 'team1'
                  ? 'border-gold bg-gold/10'
                  : 'border-white/10 bg-surface/30 hover:border-white/20'
              }`}
              data-testid="select-team1"
            >
              <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${selectedTeam === 'team1' ? 'text-gold' : 'text-white/50'}`}>Team 1</p>
              {animalData.teamLists.team1.map((a, i) => (
                <p key={`t1-${a}`} className="text-white/60 text-sm leading-loose flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${selectedTeam === 'team1' ? 'bg-gold' : 'bg-white/20'}`} />
                  {a}
                </p>
              ))}
            </button>
            <button
              onClick={() => setSelectedTeam('team2')}
              className={`p-4 border text-left transition-all duration-300 ${
                selectedTeam === 'team2'
                  ? 'border-gold bg-gold/10'
                  : 'border-white/10 bg-surface/30 hover:border-white/20'
              }`}
              data-testid="select-team2"
            >
              <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${selectedTeam === 'team2' ? 'text-gold' : 'text-white/50'}`}>Team 2</p>
              {animalData.teamLists.team2.map((a, i) => (
                <p key={`t2-${a}`} className="text-white/60 text-sm leading-loose flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${selectedTeam === 'team2' ? 'bg-gold' : 'bg-white/20'}`} />
                  {a}
                </p>
              ))}
            </button>
          </div>

          {/* Add */}
          <div className="flex items-center gap-4 flex-wrap">
            <button
              data-testid="add-full-interactive"
              onClick={() => {
                const teamLabel = selectedTeam === 'team1' ? 'Team 1' : selectedTeam === 'team2' ? 'Team 2' : '';
                addItem({
                  ...pkg,
                  hours: 1,
                  price: pkg.price,
                  name: teamLabel ? `${pkg.name} — ${teamLabel}` : pkg.name,
                  selectedTeam,
                });
              }}
              disabled={!selectedTeam}
              className={`px-6 py-3 text-xs font-semibold uppercase tracking-wide transition-colors duration-300 ${
                selectedTeam
                  ? 'bg-gold text-background hover:bg-gold-light'
                  : 'bg-white/10 text-white/30 cursor-not-allowed'
              }`}
            >
              <Plus className="w-3 h-3 inline mr-2" />
              {selectedTeam ? 'Add to Estimate' : 'Select a Team'}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function HandsOnSection() {
  // First 3 are the individual encounters, 4th is the package
  const encounters = animalData.handsOnEncounters.slice(0, 3);

  return (
    <section data-testid="hands-on-section" className="py-24 md:py-32 px-6 md:px-12 bg-background">
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-gold mb-4">Interactive Experiences</p>
          <h2 className="font-display text-4xl md:text-6xl text-white uppercase tracking-wide mb-3">
            Hands On Encounters
          </h2>
          <p className="text-white/50 text-base md:text-lg">Fully Interactive, Lovable Animals</p>
        </motion.div>

        {/* 3 Individual Encounter Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {encounters.map((animal, i) => (
            <EncounterCard key={animal.id} animal={animal} index={i} />
          ))}
        </div>

        {/* Exotic Animal Package - Its Own Area */}
        <ExoticPackageSection />
      </div>
    </section>
  );
}
